import React, { useCallback, useEffect, useState } from "react";
import { FileText, Loader2 } from "lucide-react";
import baseApi from "utils/axios-instance";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import { Toaster } from "components/ui/sonner";
import CallScriptsList from "components/CallScriptManager/CallScriptsList";

function CallScriptsPage() {
  const [loading, setLoading] = useState(true);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [specialty, setSpecialty] = useState("");

  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const [profileResponse, userResponse] = await Promise.all([
        baseApi.get("/profile"),
        baseApi.get("/dashboard/current-user", { withCredentials: true }),
      ]);

      setSpecialty(profileResponse.data.specialty || "");
      setTeamId(userResponse.data.team_id || null);
    } catch (error) {
      console.error("Error fetching call scripts page data:", error);
      toast.error("Failed to load call scripts.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  return (
    <div className="w-full">
      <Toaster richColors position="top-center" />
      <div className="p-6 max-w-4xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-6">
            <div className="p-3 bg-[#005151] rounded-xl">
              <FileText className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Call Scripts</h1>
              <p className="text-gray-600 mt-1">
                Manage your AI call script questions and order
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <Card className="border border-gray-200 shadow-sm">
            <CardContent className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-[#005151] mr-2" />
              <span className="text-gray-600">Loading call scripts...</span>
            </CardContent>
          </Card>
        ) : !teamId ? (
          <Card className="border border-gray-200 shadow-sm">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <p className="text-gray-600">
                Call scripts are unavailable without a team account.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="border border-gray-200 shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-semibold text-gray-900">
                Your Call Scripts
              </CardTitle>
              <CardDescription className="text-gray-600">
                Customize the questions our AI asks during insurance calls
              </CardDescription>
            </CardHeader>
            <CardContent>
              <CallScriptsList teamId={teamId} specialty={specialty} />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

export default CallScriptsPage;
